import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useMemo, useState } from "react";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { FaSearch } from "react-icons/fa";
import { IoColorWand } from "react-icons/io5";
import { MdClose, MdOutlineSnooze } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import {
  EngineeringTicketType,
  IssueQueryResultsType,
  useGetFieldValuesQuery,
  useSearchUsingQueryQuery,
  useStartAutofixMutation,
} from "../__generated__/graphql";
import ApplicationPage from "../components/ApplicationPage";
import CtaButton from "../components/Buttons/CtaButton";
import EngineeringTicketLink from "../components/EngineeringTicketLink";
import Table from "../components/Table";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import {
  FiltersFromMap,
  getFilterKeyMap,
  useTableParams,
} from "../hooks/useTableParams";
import formatRelativeTime from "../utils/dateFns";
import { isFilterActive } from "../utils/filters";
import {
  DateFilterValue,
  MultiSelectValue,
  TableColumn,
} from "../utils/interfaces";
import SearchQuery from "../utils/SearchQuery";

const SYSTEM = "SailfishAI System";
const AUTOFIX_FINISHED_STATES = ["Succeeded", "Failed"];
const COLUMNS: Array<TableColumn<string>> = [
  {
    id: "title",
    label: "Title & Description",
    format: (value: string, row: Record<string, any>) => {
      return (
        <div className='max-w-40'>
          <div
            title={value}
            className='text-primary-blue-100 text-sm font-semibold truncate'
          >
            {value}
          </div>
          {row.description && (
            <div title={row.description} className='text-xs truncate'>
              {row.description}
            </div>
          )}
        </div>
      );
    },
  },
  {
    id: "lastSeen",
    label: "Last detected",
    order: true,
    filter: {
      type: "dateRange",
      options: {
        title: "Last Seen",
        disableFuture: true,
      },
    },
    format: (value: string) => formatRelativeTime(value),
  },
  {
    id: "createdAt",
    label: "Date created",
    order: true,
    filter: {
      type: "dateRange",
      options: {
        title: "Created in",
        disableFuture: true,
      },
    },
    format: (value: string) => formatRelativeTime(value),
  },
  {
    id: "createdBy",
    label: "Created by",
    align: "center",
    filter: {
      type: "select",
      options: {
        label: "Created by",
        multiple: true,
      },
    },
    format: (value: string, row: Record<string, any>) => {
      if ([SYSTEM, "Unknown"].includes(value)) return value;
      return (
        <div className='flex justify-center'>
          <img
            title={row.createdByUser?.email}
            src={row.createdByUser?.avatarUrl}
            alt={row.createdByUser?.email}
            className='w-8 h-8 rounded-full'
            data-testid='userAvatar'
          />
        </div>
      );
    },
  },
  {
    id: "severity",
    label: "Severity",
    align: "center",
    format: () => (
      <div className='flex justify-center'>
        <AiOutlineExclamationCircle size={18} className='text-zinc-400' />
      </div>
    ),
  },
  {
    id: "snoozedMode",
    label: "Status",
    align: "center",
    filter: {
      type: "boolean",
      options: {
        label: "Is snoozed?",
      },
    },
    format: (value: string) => (
      <div className='flex justify-center'>
        <MdOutlineSnooze
          size={18}
          className={value === "Disabled" ? "text-gray-200" : "text-[#63A6DE]"}
        />
      </div>
    ),
  },
  {
    id: "engineeringTicket",
    label: "Associated Tickets",
    align: "center",
    filter: {
      type: "boolean",
      options: {
        label: "Has Associated Ticket?",
      },
    },
    format: (ticket: EngineeringTicketType) => (
      <div className='flex justify-center'>
        <EngineeringTicketLink ticket={ticket} />
      </div>
    ),
  },
];
const filterKeyMap = getFilterKeyMap(COLUMNS);
type FilterKeyMap = typeof filterKeyMap;

const getSearchQuery = (
  {
    createdAt,
    createdBy,
    engineeringTicket,
    snoozedMode,
    lastSeen,
  }: FiltersFromMap<FilterKeyMap>,
  search: string,
) => {
  const searchQuery = new SearchQuery();

  if (isFilterActive("dateRange", lastSeen)) {
    searchQuery.addDateRangeParameter("last_seen", lastSeen as DateFilterValue);
  }

  if (isFilterActive("dateRange", createdAt)) {
    searchQuery.addDateRangeParameter(
      "created_at",
      createdAt as DateFilterValue,
    );
  }

  if (isFilterActive("select", createdBy)) {
    searchQuery.addSearchParameters(
      "created_by_user__email",
      "==",
      createdBy as MultiSelectValue,
    );
  }

  if (snoozedMode) {
    searchQuery.addSearchParameter("snoozed_mode", "!=", "Disabled");
  }

  if (engineeringTicket) {
    searchQuery.addSearchParameter("engineering_ticket__id", "!=", "Null");
  }

  if (search) {
    searchQuery.addLookup(["title", "description"], search);
  }

  return searchQuery.toString();
};

const IssuesPage = () => {
  const { selectedCompany } = useCompany();
  const [fixingIssues, setFixingIssues] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const { isSailfishQAMember } = useAuth();
  const navigate = useNavigate();

  const {
    filters,
    setFilters,
    search,
    setSearch,
    debouncedSearch,
    pagination,
    setPagination,
    ordering,
    setOrdering,
  } = useTableParams({
    filterKeyMap,
    defaultOrdering: { orderBy: "lastSeen" },
    storageKey: "issues",
  });

  const { data } = useGetFieldValuesQuery({
    variables: {
      searchEntity: "Issues",
      fieldName: "created_by_user__email",
      ...(selectedCompany && {
        companyId: parseInt(selectedCompany.value, 10),
      }),
    },
  });
  const [startAutofix] = useStartAutofixMutation();

  const creators = data?.getFieldValues || [];

  const {
    loading,
    updateQuery,
    data: searchData,
    refetch,
  } = useSearchUsingQueryQuery({
    variables: {
      searchEntity: "issues",
      searchQuery: getSearchQuery(filters, debouncedSearch),
      limit: pagination.rowsPerPage,
      offset: pagination.page * pagination.rowsPerPage,
      companyId: selectedCompany ? parseInt(selectedCompany.value, 10) : 0,
      orderBy: `${ordering.order === "asc" ? "-" : ""}${
        ordering.orderBy === "lastSeen" ? "last_seen" : "created_at"
      }`,
    },
  });

  const searchUsingQuery =
    searchData?.searchUsingQuery as IssueQueryResultsType;
  const results = searchUsingQuery?.results?.items || [];
  const totalItemsCount = searchUsingQuery?.results?.totalItemsCount || 0;

  const handleRowClick = (id: string) => {
    navigate(`/issues/${id}`);
  };

  const selectedToFix = selected.filter((id) => {
    if (fixingIssues.includes(id)) return false;
    const issue = results.find((issue) => issue.id == id);
    if (!issue) return false;
    if (issue.autofixRuns || !issue.canAutofix || !issue.hasExceptionRecords)
      return false;
    return true;
  });

  const updateAutofixState = (ids: string[], state: string) =>
    updateQuery((data) => {
      const searchUsingQuery = data?.searchUsingQuery as IssueQueryResultsType;
      searchUsingQuery.results.items.map((item) => {
        item.autofixRuns?.state;
      });
      return {
        ...data,
        searchUsingQuery: {
          ...searchUsingQuery,
          results: {
            ...searchUsingQuery.results,
            items: searchUsingQuery.results.items.map((item) => {
              if (!ids.includes(item.id)) return item;
              return {
                ...item,
                autofixRuns: { state },
              };
            }),
          },
        },
      };
    });

  const handleFixIssues = async (ids: string[]) => {
    try {
      setFixingIssues((prev) => [...prev, ...ids]);
      await Promise.all(
        ids.map(async (id) => {
          await startAutofix({
            variables: {
              issueId: parseInt(id, 10),
              companyId: selectedCompany
                ? parseInt(selectedCompany.value, 10)
                : undefined,
            },
          });
        }),
      );

      // Update cache
      updateAutofixState(ids, "queued");
    } catch (err) {
      console.error("Auto-fix failed", err);
    } finally {
      setFixingIssues((prev) => prev.filter((id) => !ids.includes(id)));
    }
  };

  const autofixInProgress = results.some(
    ({ autofixRuns }) =>
      !!autofixRuns && !AUTOFIX_FINISHED_STATES.includes(autofixRuns.state),
  );

  // Refetch issues if there is autofix in progress
  useEffect(() => {
    if (!autofixInProgress) return;

    const interval = setInterval(() => refetch(), 10000);
    return () => clearInterval(interval);
  }, [autofixInProgress]);

  const dynamicFiltersOptions = useMemo(
    () => ({
      createdBy: creators.map((creator) => ({
        id: creator,
        name: creator,
      })),
    }),
    [creators],
  );

  return (
    <ApplicationPage pageHeaderContent='Issues'>
      <div className='h-[calc(100%-44px)]'>
        <div className='flex gap-4 pb-4 justify-between'>
          <TextField
            size='small'
            variant='outlined'
            placeholder='Search by title & description'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className='w-72'
            classes={{
              root: "[&>div>input]:text-sm [&>div>input]:py-1.5",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <FaSearch size={16} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position='end'
                  className={search ? "visible" : "invisible"}
                >
                  <MdClose
                    size={16}
                    className='cursor-pointer'
                    onClick={() => setSearch("")}
                  />
                </InputAdornment>
              ),
            }}
            data-testid='searchField'
          />
          <Tooltip
            title={selected.length ? "" : "Select issues to fix"}
            placement='top'
          >
            <div className='flex items-center'>
              <CtaButton
                titlePrimary='Autofix selected'
                icon={<IoColorWand size={18} />}
                bgPrimaryClass='bg-primary-blue-100'
                textPrimaryClass='text-white'
                bgPrimaryHoverClass='hover:bg-primary-blue-100'
                textPrimaryHoverClass='hover:text-white'
                onClick={() => handleFixIssues(selectedToFix)}
                showOutlineHoverPrimary={false}
                additionalClassNames='h-8 rounded-md text-sm leading-6 font-medium shadow-md'
                additionalWidth='24px'
                disabled={!selectedToFix.length}
                startIcon
                data-testid='autofixSelected'
              />
            </div>
          </Tooltip>
        </div>
        <div className='h-[calc(100%-48px)]'>
          <Table
            columns={COLUMNS}
            count={totalItemsCount}
            idKey='id'
            loading={loading}
            order={ordering.order}
            orderBy={ordering.orderBy}
            page={pagination.page}
            rows={results}
            rowsPerPage={pagination.rowsPerPage}
            handleChangeOrder={setOrdering}
            handleChangeRowsPerPage={(rowsPerPage: number) =>
              setPagination((prev) => ({ ...prev, rowsPerPage }))
            }
            onClick={handleRowClick}
            setPage={(page: number) =>
              setPagination((prev) => ({ ...prev, page }))
            }
            checkboxSelection
            selected={selected}
            setSelected={setSelected}
            filters={filters}
            setFilters={setFilters}
            dynamicFiltersOptions={dynamicFiltersOptions}
            {...{
              rowAction: ({
                id,
                autofixRuns,
                canAutofix,
                hasExceptionRecords,
              }) => {
                const inProgress =
                  fixingIssues.includes(id) ||
                  (!!autofixRuns &&
                    !AUTOFIX_FINISHED_STATES.includes(autofixRuns.state));
                const finished =
                  !!autofixRuns &&
                  AUTOFIX_FINISHED_STATES.includes(autofixRuns.state);
                return (
                  <Tooltip
                    title={
                      canAutofix
                        ? hasExceptionRecords
                          ? ""
                          : "Full-Stack Auto-Fix coming soon"
                        : "Connect GitHub and Slack to enable Auto-Fix"
                    }
                    placement='top'
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // ← prevent the row-link
                        handleFixIssues([id]);
                      }}
                      disabled={
                        inProgress ||
                        finished ||
                        !canAutofix ||
                        !hasExceptionRecords
                      }
                      className='flex items-center justify-center whitespace-nowrap gap-2 font-bold px-4 py-1 w-[120px] bg-[#1976D2] text-white text-sm leading-6 rounded shadow-md disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      {inProgress && (
                        <span className='h-5'>
                          <ClipLoader
                            size={18}
                            speedMultiplier={0.8}
                            color='white'
                          />
                        </span>
                      )}
                      {!finished && !inProgress && <IoColorWand size={18} />}
                      {finished
                        ? autofixRuns.state
                        : inProgress
                        ? "Fixing..."
                        : "AutoFix"}
                    </button>
                  </Tooltip>
                );
              },
              rowActionLabel: "Actions",
            }}
          />
        </div>
      </div>
    </ApplicationPage>
  );
};

export default IssuesPage;
